/**
 * Ombraz Product Filter System
 * This script handles the filtering functionality for Ombraz products.
 * It allows users to filter products based on frame styles, gear, frame sizes, and lens options.
 */

// EOL SKUs array - Discontinued SKU variants that should be removed from display
// Loaded from theme settings (window.EOL_SKUS) - falls back to empty array if not set
const COLLECTION_FILTER_EOL_SKUS = window.EOL_SKUS || [];

// Helper function to check if a variant is EOL
function isCollectionFilterEOLVariant(variant) {
  if (!variant || !variant.sku) return false;
  return COLLECTION_FILTER_EOL_SKUS.includes(String(variant.sku));
}

let isMobile = window.matchMedia("(max-width: 1199px)").matches;

// If need to debug, set to true
window.OMBRAZ_DEBUG = false;

// Global variable to track frame style check order (most recent first)
let frameStyleCheckOrder = [];

// Global variable to track gear check order (most recent first)
let gearCheckOrder = [];
let hasCollectionFilterInitialized = false;

function dispatchLimitedEditionModeChange(active) {
  document.dispatchEvent(
    new CustomEvent("ombraz:limited-edition-mode-change", {
      detail: { active: Boolean(active) },
    }),
  );
}

function productSupportsLimitedEdition(product) {
  if (!product) return false;

  return (
    product.dataset.hasLimitedEditionOptions === "true" ||
    product.dataset.limitedEdition === "true"
  );
}

// Main initialization on DOMContentLoaded
document.addEventListener("DOMContentLoaded", function () {
  if (hasCollectionFilterInitialized) return;
  hasCollectionFilterInitialized = true;

  // Set isMobile based on current viewport
  isMobile = window.matchMedia("(max-width: 1199px)").matches;

  initializeAccordion();
  initializeFilters();

  // Check URL parameters first, then handle collection-specific filtering
  const urlParams = new URLSearchParams(window.location.search);
  const hasURLFilters =
    urlParams.has("styles") ||
    urlParams.has("sizes") ||
    urlParams.has("lenses") ||
    urlParams.has("colors") ||
    urlParams.has("gear") ||
    urlParams.has("limitededition") ||
    urlParams.has("bestseller");

  if (hasURLFilters) {
    // If we have URL parameters, prioritize filter restoration
    debugLog("Found URL parameters, attempting filter restoration");

    const filtersRestored = restoreFiltersFromURL();

    if (!filtersRestored) {
      // If restoration failed, fall back to URL-based filtering
      handleURLBasedFiltering();
    }
  } else {
    // No URL parameters, handle size parameter or collection-specific filtering
    const sizeParameterHandled = handleSizeURLParameter();

    if (!sizeParameterHandled) {
      handleURLBasedFiltering();
    }
  }

  // Ensure frame size option states are updated AFTER all initialization logic (including other scripts) has run
  setTimeout(() => {
    const cards = document.querySelectorAll(".ombraz-product-v2__card");
    cards.forEach((card) => {
      // Determine if a specific filter is active or just general view
      // If we don't have explicit filters, pass [] to infer from visibility
      updateFrameSizeOptionState(card, []);
    });

    // Ensure at least one slide is active for every card
    ensureActiveSlides();
  }, 100);
});

// Update isMobile when window resizes
window.addEventListener("resize", function () {
  isMobile = window.matchMedia("(max-width: 1199px)").matches;
});

/**
 * Ensures that every product card with slides has at least one slide marked as 'is-active'.
 * Fixes issue where single-slide products might not show the active border state.
 */
function ensureActiveSlides() {
  const cards = document.querySelectorAll(".ombraz-product-v2__card");
  cards.forEach((card) => {
    const slides = card.querySelectorAll(".ombraz-product-v2-slide");
    if (slides.length > 0) {
      const activeSlide = card.querySelector(
        ".ombraz-product-v2-slide.is-active",
      );
      if (!activeSlide) {
        // If no slide is active, activate the first one
        slides[0].classList.add("is-active");
      }
    }
  });
}

function saveFiltersToURL() {
  // Get active filters
  const frameStyles = getActiveFilters("frame-style");
  const frameSizes = getActiveFilters("frame-size");
  const lensOptions = getActiveFilters("lens-option");
  const frameColors = getActiveFilters("frame-color");
  const gearItems = getActiveFilters("gear");
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;
  const limitedEditionChecked =
    document.querySelector('.ombraz-limited-edition input[type="checkbox"]')
      ?.checked || false;

  // Create URL parameters
  const params = new URLSearchParams();

  // Only add parameters that have values
  if (frameStyles.length > 0) params.set("styles", frameStyles.join(","));
  if (frameSizes.length > 0) params.set("sizes", frameSizes.join(","));
  if (lensOptions.length > 0) params.set("lenses", lensOptions.join(","));
  if (frameColors.length > 0) params.set("colors", frameColors.join(","));
  if (gearItems.length > 0) params.set("gear", gearItems.join(","));
  if (bestSellerChecked) params.set("bestseller", "true");
  if (limitedEditionChecked) params.set("limitededition", "true");

  // Update the URL without refreshing
  if (params.toString()) {
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
    debugLog("Filter state saved to URL", params.toString());
  } else {
    // If no filters, remove any query parameters
    window.history.replaceState({}, "", window.location.pathname);
  }
}


// Improved restoreFiltersFromURL function
function restoreFiltersFromURL() {
  debugLog("Starting restoreFiltersFromURL");

  // Get parameters from URL
  const params = new URLSearchParams(window.location.search);
  let hasRestoredFilters = false;

  // Create an object to track which filters we've restored
  const restoredFilters = {
    "frame-style": [],
    "frame-size": [],
    "lens-option": [],
    "frame-color": [],
    gear: [],
  };

  // Helper function to check filter options
  function checkFilter(type, paramName) {
    if (params.has(paramName)) {
      const values = params.get(paramName).split(",");
      values.forEach((value) => {
        const normalizedValue = value.toLowerCase().trim();
        debugLog(`Checking filter ${type} with value ${normalizedValue}`);

        // Add to our tracking object
        restoredFilters[type].push(normalizedValue);

        const checkbox = document.querySelector(
          `input[data-filter-type="${type}"][data-${type}="${normalizedValue}"]`,
        );

        if (checkbox) {
          checkbox.checked = true;
          // Open accordion containing this checkbox
          const accordion = checkbox.closest(".ombraz-accordion-item");
          if (accordion) {
            accordion.setAttribute("open", "");
            updateAccordionIcon(accordion, true);
          }
          hasRestoredFilters = true;
          debugLog(`Restored filter ${type}=${normalizedValue}`);
        } else {
          debugLog(`Could not find checkbox for ${type}=${normalizedValue}`);
        }
      });
      return true; // Indicate that we found and processed this parameter
    }
    return false; // Parameter not found
  }

  // Check all filter types
  checkFilter("frame-style", "styles");
  checkFilter("frame-size", "sizes");
  checkFilter("lens-option", "lenses");
  checkFilter("frame-color", "colors");
  checkFilter("gear", "gear");

  // Initialize frame style check order based on restored styles
  if (restoredFilters["frame-style"].length > 0) {
    frameStyleCheckOrder = [...restoredFilters["frame-style"]].reverse();
    debugLog(
      "Initialized frame style check order from URL:",
      frameStyleCheckOrder,
    );
  }

  // Initialize gear check order based on restored gear
  if (restoredFilters["gear"].length > 0) {
    gearCheckOrder = [...restoredFilters["gear"]].reverse();
    debugLog("Initialized gear check order from URL:", gearCheckOrder);
  }

  // Restore best seller checkbox
  if (params.has("bestseller") && params.get("bestseller") === "true") {
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]',
    );
    if (bestSellerCheckbox) {
      bestSellerCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored bestseller filter");
    } else {
      debugLog("Could not find bestseller checkbox");
    }
  }

  // Restore limited edition checkbox
  if (params.has("limitededition") && params.get("limitededition") === "true") {
    const limitedEditionCheckbox = document.querySelector(
      '.ombraz-limited-edition input[type="checkbox"]',
    );
    if (limitedEditionCheckbox) {
      limitedEditionCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored limited edition filter");
    } else {
      debugLog("Could not find limited edition checkbox");
    }
  }

  // If we have restored any filters, apply them with a delay to ensure DOM is ready
  if (hasRestoredFilters) {
    debugLog(
      "Restored filters from URL parameters, applying filters with delay",
      restoredFilters,
    );

    // Toggle UI buttons
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Use a longer delay to ensure DOM is fully loaded
    setTimeout(() => {
      const bestSellerChecked =
        document.querySelector('.ombraz-best-seller input[type="checkbox"]')
          ?.checked || false;
      const limitedEditionChecked =
        document.querySelector('.ombraz-limited-edition input[type="checkbox"]')
          ?.checked || false;

      // Force filter application regardless of mobile/desktop state
      if (limitedEditionChecked) {
        handleLimitedEditionFilter(true);
      } else if (bestSellerChecked) {
        handleBestSellerFilter(true);
      } else {
        // Apply filters with special handling for multiple filter types
        applyFiltersFromURL(restoredFilters);
      }

      // For mobile, close the filter sidebar if it's open
      if (isMobile) {
        const sidebar = document.getElementById("ombrazSidebar");
        if (sidebar && sidebar.classList.contains("active")) {
          closeTheFilterHederMobile();
        }
      }
    }, 500);

    return true;
  }

  return false;
}


function applyFiltersFromURL(restoredFilters) {
  debugLog(
    "applyFiltersFromURL: Starting filter application with",
    restoredFilters,
  );

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  // Check if any filter type is active
  const hasFrameStyleFilters = restoredFilters["frame-style"].length > 0;
  const hasFrameSizeFilters = restoredFilters["frame-size"].length > 0;
  const hasLensOptionFilters = restoredFilters["lens-option"].length > 0;
  const hasFrameColorFilters = restoredFilters["frame-color"].length > 0;
  const hasGearFilters = restoredFilters["gear"].length > 0;
  const mirrorFilterActive = isMirrorLensFilterActive(
    restoredFilters["lens-option"],
  );

  const anyFilterActive =
    hasFrameStyleFilters ||
    hasFrameSizeFilters ||
    hasLensOptionFilters ||
    hasFrameColorFilters ||
    hasGearFilters;

  debugLog("applyFiltersFromURL: Filter status", {
    hasFrameStyleFilters,
    hasFrameSizeFilters,
    hasLensOptionFilters,
    hasFrameColorFilters,
    hasGearFilters,
    anyFilterActive,
  });

  // Apply filters to each product item
  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
    const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
    const itemLensValue = (
      item.getAttribute("data-lens-value") || ""
    ).toLowerCase();

    // Get available sizes using our helper function
    const itemSizes = getAvailableSizes(item);

    debugLog(`Checking product: ${itemTitle}`, {
      itemStyle,
      itemSizes,
      itemLensValue,
      itemColor,
      itemGear,
    });

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    const isMirroredCard = isMirroredCollectionCard(item);
    if (mirrorFilterActive && !isMirroredCard) {
      hideGridItem(item);
      return;
    }

    if (!mirrorFilterActive && isMirroredCard) {
      hideGridItem(item);
      return;
    }

    // Special handling for gear items when gear filters are active
    if (hasGearFilters) {
      // If this is a gear item, check if it matches the selected gear filters
      if (isGearOrAccessory) {
        const matchesGear = restoredFilters["gear"].some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "nose-shield" &&
              (itemTitle.includes("nose shield") ||
                itemStyle.includes("nose-shield"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card")),
        );

        if (matchesGear) {
          showGridItem(item);
          visibleCount++;
          debugLog(`Showing gear item: ${itemTitle}`);
          return; // Skip other checks, this is a matched gear item
        } else {
          hideGridItem(item);
          debugLog(`Hiding gear item (no match): ${itemTitle}`);
          return; // Skip other checks, this is an unmatched gear item
        }
      } else {
        // This is not a gear item but gear filter is active, hide it
        hideGridItem(item);
        debugLog(`Hiding non-gear item: ${itemTitle}`);
        return;
      }
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    if (
      isGearOrAccessory &&
      (hasFrameSizeFilters ||
        hasFrameStyleFilters ||
        hasLensOptionFilters ||
        hasFrameColorFilters)
    ) {
      hideGridItem(item);
      debugLog(`Hiding gear item (other filters active): ${itemTitle}`);
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame style filters
    if (hasFrameStyleFilters) {
      if (!restoredFilters["frame-style"].includes(itemStyle)) {
        matchesAllFilters = false;
        debugLog(
          `Style mismatch for ${itemTitle}: ${itemStyle} not in ${restoredFilters["frame-style"]}`,
        );
      }
    }

    // Check frame size filters
    if (matchesAllFilters && hasFrameSizeFilters) {
      // Use the helper function to get available sizes
      const itemSizes = getAvailableSizes(item);

      const matchesSize = restoredFilters["frame-size"].some((size) => {
        const lowerSize = size.toLowerCase();

        // Check if any of the item's available sizes match the filter
        return itemSizes.some((availableSize) => {
          const lowerAvailableSize = availableSize.toLowerCase();

          if (lowerSize === "narrow") {
            return lowerAvailableSize.includes("narrow");
          } else if (lowerSize === "regular") {
            return lowerAvailableSize.includes("regular");
          } else if (lowerSize === "xl") {
            return (
              lowerAvailableSize.includes("xl") ||
              lowerAvailableSize.includes("extra large")
            );
          }

          return lowerAvailableSize === lowerSize;
        });
      });

      if (!matchesSize) {
        matchesAllFilters = false;
      }
    }

    // Check lens option filters
    if (matchesAllFilters && hasLensOptionFilters) {
      const matchesLensOption = restoredFilters["lens-option"].some(
        (option) => {
          if (option === "polarized") {
            return (
              itemLensValue.includes("polarized") &&
              !itemLensValue.includes("non-polarized") &&
              !itemLensValue.includes("mirror")
            );
          } else if (option === "non-polarized") {
            return (
              itemLensValue.includes("non-polarized") &&
              !itemLensValue.includes("mirror")
            );
          } else if (
            option === "blue-blocking" ||
            option === "low light" ||
            option === "low-light"
          ) {
            return (
              itemLensValue.includes("blue-blocking") ||
              itemLensValue.includes("low light") ||
              itemLensValue.includes("low-light")
            );
          }

          return itemLensValue.includes(option);
        },
      );

      if (!matchesLensOption) {
        matchesAllFilters = false;
        debugLog(
          `Lens mismatch for ${itemTitle}: ${itemLensValue} does not match any of ${restoredFilters["lens-option"]}`,
        );
      }
    }

    // Check frame color filters
    if (matchesAllFilters && hasFrameColorFilters) {
      if (!restoredFilters["frame-color"].includes(itemColor)) {
        matchesAllFilters = false;
        debugLog(
          `Color mismatch for ${itemTitle}: ${itemColor} not in ${restoredFilters["frame-color"]}`,
        );
      }
    }

    // Apply visibility directly without deduplication for regular products
    if (matchesAllFilters) {
      showGridItem(item);
      visibleCount++;
      debugLog(`Showing product: ${itemTitle}`);
    } else {
      hideGridItem(item);
      debugLog(`Hiding product (filter mismatch): ${itemTitle}`);
    }
  });

  debugLog(
    `applyFiltersFromURL: Filter applied, ${visibleCount} items visible`,
  );

  // Reorder products based on frame style check order if multiple frame styles are checked
  if (
    restoredFilters["frame-style"].length > 1 &&
    frameStyleCheckOrder.length > 1
  ) {
    reorderProductsByFrameStyle(productGrid, restoredFilters["frame-style"]);
  }

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Count all visible products across all containers for accurate no-results display
  const totalVisibleCount = getTotalVisibleProductCount();
  debugLog(
    `applyFiltersFromURL: Total visible products across all containers: ${totalVisibleCount}`,
  );

  // Show or hide "No results" message
  const showNoResults = totalVisibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // Auto-select frame sizes if filter is active
  if (hasFrameSizeFilters) {
    autoSelectFrameSizes(restoredFilters["frame-size"]);
  } else {
    // No frame-size filters restored -> reset card radios to defaults
    resetFrameSizeSelection();
  }
}

/**
 * Auto-selects frame size radio buttons on visible product cards
 * @param {Array} frameSizes - Array of frame sizes to select (e.g., ['narrow', 'xl'])
 */
function autoSelectFrameSizes(frameSizes) {
  if (!frameSizes || frameSizes.length === 0) return;

  debugLog("autoSelectFrameSizes: Selecting sizes", frameSizes);

  // Get all visible product cards
  const visibleCards = document.querySelectorAll(
    ".ombraz-product-v2__grid-item:not(.hide-this)",
  );

  visibleCards.forEach((item) => {
    const card = item.querySelector(".ombraz-product-v2__card");
    if (!card) return;

    // Determine which size to select based on filters
    let sizeToSelect = null;

    // If only one size is in the filter, use that
    if (frameSizes.length === 1) {
      sizeToSelect = frameSizes[0];
    } else {
      // If multiple sizes, check availability and select first available
      for (const size of frameSizes) {
        const isAvailable =
          card.getAttribute(`data-is-${size}-available`) === "true";
        if (isAvailable) {
          sizeToSelect = size;
          break;
        }
      }
    }

    if (!sizeToSelect) return;

    // Find and click the corresponding radio button
    const radioButton = card.querySelector(
      `input[type="radio"][id^="${sizeToSelect}"]`,
    );

    if (radioButton && !radioButton.checked) {
      debugLog(`Auto-selecting ${sizeToSelect} for card`, card);
      radioButton.checked = true;
      // Trigger change event to update product info
      radioButton.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // Update the 'is-active' state on frame size options based on current state
    // We do this AFTER potential changes so visibility updates from item-collection-card.js can happen
    setTimeout(() => {
      updateFrameSizeOptionState(card, frameSizes);
    }, 0);
  });
}

/**
 * Reset frame size radios on all product cards back to their default selection
 * (prefer Regular if present, otherwise first available).
 */
function resetFrameSizeSelection() {
  const cards = document.querySelectorAll(".ombraz-product-v2__card");
  if (!cards || cards.length === 0) return;

  cards.forEach((card) => {
    // Try to find the regular/narrow/xl radios by id prefix
    const regular = card.querySelector('input[id^="regular-"]');
    const narrow = card.querySelector('input[id^="narrow-"]');
    const xl = card.querySelector('input[id^="xl-"]');

    const defaultRadio = regular || narrow || xl;
    if (!defaultRadio) return;

    if (!defaultRadio.checked) {
      debugLog("resetFrameSizeSelection: resetting card to default radio", {
        card,
        defaultId: defaultRadio.id,
      });
      defaultRadio.checked = true;
      defaultRadio.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // Update state after resetting
    setTimeout(() => {
      updateFrameSizeOptionState(card, []);
    }, 0);
  });
}

/**
 * Updates the 'is-active' class on frame size options container based on availability
 * AND applies 'is-active' to the selected label.
 * @param {HTMLElement} card - The product card element
 * @param {Array} frameSizes - Optional array of filtered frame sizes
 */
function updateFrameSizeOptionState(card, frameSizes = []) {
  if (!card) return;

  const frameSizeOptions = card.querySelector(
    ".ombraz-product-v2__frame-size-options",
  );
  if (!frameSizeOptions) return;

  // Check if we should activate the container (single available option)
  // We use visible labels as proxy for availability since item-collection-card.js handles visibility
  const labels = Array.from(frameSizeOptions.querySelectorAll("label"));
  const visibleLabels = labels.filter((label) => {
    return (
      label.style.display !== "none" && label.style.visibility !== "hidden"
    );
  });

  // Also check if filtering explicitly by one size, regardless of visibility (though usually they align)
  const isSingleFilter = frameSizes && frameSizes.length === 1;

  if (visibleLabels.length === 1 || isSingleFilter) {
    frameSizeOptions.classList.add("is-active");
  } else {
    frameSizeOptions.classList.remove("is-active");
  }

  // Update active state on labels based on checked input
  labels.forEach((label) => {
    const input = label.querySelector("input[type='radio']");
    if (input && input.checked) {
      label.classList.add("is-active");
    } else {
      label.classList.remove("is-active");
    }
  });
}

/**
 * Adds change listeners to frame size radios to update the 'is-active' class on labels
 */
function initFrameSizeRadioListeners() {
  const cards = document.querySelectorAll(".ombraz-product-v2__card");
  cards.forEach((card) => {
    const radios = card.querySelectorAll("input[name^='frame-size-']");
    radios.forEach((radio) => {
      // Remove existing listener to avoid duplicates if re-initialized?
      // Cloning node implies we might lose other listeners, better to just add.
      // Or use a flag. For now, simple add.
      radio.addEventListener("change", () => {
        // Find parent label and add class, remove from others
        const optionsContainer = card.querySelector(
          ".ombraz-product-v2__frame-size-options",
        );
        if (optionsContainer) {
          const labels = optionsContainer.querySelectorAll("label");
          labels.forEach((l) => l.classList.remove("is-active"));

          const parentLabel = radio.closest("label");
          if (parentLabel) parentLabel.classList.add("is-active");

          // Also re-evaluate container state
          // Pass empty array as we don't know the filters here, but it should infer from visibility/checked state
          // Small timeout to allow visibility updates
          setTimeout(() => {
            updateFrameSizeOptionState(card, []);
          }, 0);
        }
      });
    });
  });
}

function initializeFilters() {
  const filterWrapper = document.getElementById("ombrazFilterWrapper");
  if (!filterWrapper || filterWrapper.dataset.filterEventsInit === "true") return;
  filterWrapper.dataset.filterEventsInit = "true";

  const filterCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"]',
  );
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]',
  );
  const limitedEditionCheckbox = document.querySelector(
    '.ombraz-limited-edition input[type="checkbox"]',
  );
  const productItems = document.querySelectorAll(
    ".ombraz-product-v2__grid-item",
  );
  const closeMobileFilterIfActive = () => {
    if (!isMobile) return;
    const sidebar = document.getElementById("ombrazSidebar");
    if (!sidebar || !sidebar.classList.contains("active")) return;
    closeTheFilterHederMobile();
  };

  // Hide both buttons initially
  toggleClearButton(false);
  toggleShowResultsButton(false);

  // Initialize frame size radio listeners for label classes
  initFrameSizeRadioListeners();

  // Add change event listener to Best Seller checkbox
  if (bestSellerCheckbox) {
    bestSellerCheckbox.addEventListener("change", () => {
      // Uncheck all other filter checkboxes
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]',
      );
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // Uncheck limited edition checkbox if checked
      if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
        limitedEditionCheckbox.checked = false;
        dispatchLimitedEditionModeChange(false);
      }

      // Show Show Results button on mobile (will be hidden later if not needed)
      toggleShowResultsButton(bestSellerCheckbox.checked);

      // Apply filter immediately on both desktop and mobile
      handleBestSellerFilter();

      // Save filter state to URL
      saveFiltersToURL();
      closeMobileFilterIfActive();
    });
  }

  // Add change event listener to Limited Edition checkbox
  if (limitedEditionCheckbox) {
    limitedEditionCheckbox.addEventListener("change", () => {
      // Uncheck best seller checkbox if checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]",
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Check if any other filters are active
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]',
      );

      // Uncheck all other filter checkboxes
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      const hasOtherActiveFilters = Array.from(otherFilterCheckboxes).some(
        (checkbox) => checkbox.checked,
      );

      // Show Show Results button based on checkbox state and other filters
      toggleShowResultsButton(
        limitedEditionCheckbox.checked || hasOtherActiveFilters,
      );
      toggleClearButton(
        limitedEditionCheckbox.checked || hasOtherActiveFilters,
      );

      // Apply filter immediately on both desktop and mobile
      handleLimitedEditionFilter();

      // Save filter state to URL
      saveFiltersToURL();
      closeMobileFilterIfActive();
    });
  }

  // Show all products by default
  if (productItems.length) {
    productItems.forEach((item) => {
      if (isMirroredCollectionCard(item)) {
        hideGridItem(item);
      } else {
        showGridItem(item);
      }
    });
  }

  // Add checkbox change event listener for regular filters (frame style, size, lens, color, gear)
  if (filterCheckboxes.length) {
    filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => {
        // Uncheck Limited Edition checkbox if checked
        if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
          limitedEditionCheckbox.checked = false;
          dispatchLimitedEditionModeChange(false);
        }

        // Uncheck Best Seller checkbox if checked
        if (bestSellerCheckbox && bestSellerCheckbox.checked) {
          bestSellerCheckbox.checked = false;

          // IMPORTANT: Hide Best Seller section and show regular grid
          const bestSellerProducts = document.querySelector(
            "[data-custom-products]",
          );
          if (bestSellerProducts) {
            bestSellerProducts.classList.add("hide-this", "visually-hidden");
          }

          const regularProductGrid = document.querySelector(
            ".ombraz-product-v2__grid",
          );
          if (regularProductGrid) {
            regularProductGrid.classList.remove("hide-this", "visually-hidden");
          }
        }

        // If this is a gear filter being checked, uncheck all non-gear filters
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          // Uncheck all frame style filters
          const frameStyleCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-style"]:checked',
          );
          frameStyleCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all frame size filters
          const frameSizeCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-size"]:checked',
          );
          frameSizeCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all lens option filters
          const lensOptionCheckboxes = document.querySelectorAll(
            'input[data-filter-type="lens-option"]:checked',
          );
          lensOptionCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all frame color filters
          const frameColorCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-color"]:checked',
          );
          frameColorCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });
        }

        // If this is a frame-style filter being checked, update the check order
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "frame-style"
        ) {
          const frameStyleValue = checkbox.getAttribute("data-frame-style");
          if (frameStyleValue) {
            frameStyleCheckOrder = frameStyleCheckOrder.filter(
              (style) => style !== frameStyleValue,
            );
            frameStyleCheckOrder.unshift(frameStyleValue);
            debugLog("Updated frame style check order:", frameStyleCheckOrder);
          }
        }

        // If this is a frame-style filter being unchecked, remove from order
        if (
          !checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "frame-style"
        ) {
          const frameStyleValue = checkbox.getAttribute("data-frame-style");
          if (frameStyleValue) {
            frameStyleCheckOrder = frameStyleCheckOrder.filter(
              (style) => style !== frameStyleValue,
            );
            debugLog("Removed from frame style check order:", frameStyleValue);
          }
        }

        // If this is a gear filter being checked, update the check order
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          const gearValue = checkbox.getAttribute("data-gear");
          if (gearValue) {
            gearCheckOrder = gearCheckOrder.filter((gear) => gear !== gearValue);
            gearCheckOrder.unshift(gearValue);
            debugLog("Updated gear check order:", gearCheckOrder);
          }
        }

        // If this is a gear filter being unchecked, remove from order
        if (
          !checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          const gearValue = checkbox.getAttribute("data-gear");
          if (gearValue) {
            gearCheckOrder = gearCheckOrder.filter((gear) => gear !== gearValue);
            debugLog("Removed from gear check order:", gearValue);
          }
        }

        // If this is a non-gear filter being checked, uncheck all gear filters
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") !== "gear"
        ) {
          const gearCheckboxes = document.querySelectorAll(
            'input[data-filter-type="gear"]:checked',
          );
          gearCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });
        }

        const hasCheckedBoxes = Array.from(filterCheckboxes).some(
          (cb) => cb.checked,
        );

        toggleClearButton(hasCheckedBoxes);
        applyFilters(event);

        saveFiltersToURL();
        closeMobileFilterIfActive();
      });
    });
  }

  // Clear all filters
  clearAllFilters();
}


// Debug helper function to log filter issues to console
function debugLog(message, data = null) {
  if (window.OMBRAZ_DEBUG) {
    if (data) {
      console.log(`OMBRAZ DEBUG: ${message}`, data);
    } else {
      console.log(`OMBRAZ DEBUG: ${message}`);
    }
  }
}

function isMirroredCollectionCard(item) {
  return item?.getAttribute("data-mirrored-card") === "true";
}

function isMirrorLensFilterActive(lensOptions = []) {
  return lensOptions.includes("mirror");
}

function showGridItem(item) {
  item.classList.remove("hide-this", "visually-hidden");
}

function hideGridItem(item) {
  item.classList.add("hide-this");
  if (isMirroredCollectionCard(item)) {
    item.classList.add("visually-hidden");
  }
}

function applyFilters(event) {
  debugLog("applyFilters: Starting filter application");

  // Get all filter inputs
  const filterInputs = document.querySelectorAll("input[data-filter-type]");

  // Check if any filter is checked
  const anyFilterActive = Array.from(filterInputs).some(
    (input) => input.checked,
  );

  // Get all checked filter values
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");
  const mirrorFilterActive = isMirrorLensFilterActive(checkedLensOptions);

  // Check if any gear filter is active
  const isGearFilterActive = checkedGearItems.length > 0;

  debugLog("applyFilters: Active filters", {
    anyFilterActive,
    checkedFrameStyles,
    checkedFrameSizes,
    checkedLensOptions,
    checkedFrameColors,
    checkedGearItems,
  });

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    const isMirroredCard = isMirroredCollectionCard(item);
    if (mirrorFilterActive && !isMirroredCard) {
      hideGridItem(item);
      return;
    }

    if (!mirrorFilterActive && isMirroredCard) {
      hideGridItem(item);
      return;
    }

    // Special handling for gear items when gear filters are active
    if (isGearFilterActive) {
      if (isGearOrAccessory) {
        const matchesGear = checkedGearItems.some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "nose-shield" &&
              (itemTitle.includes("nose shield") ||
                itemStyle.includes("nose-shield"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card")),
        );

        if (matchesGear) {
          showGridItem(item);
          visibleCount++;
          return;
        }

        hideGridItem(item);
        return;
      }

      hideGridItem(item);
      return;
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    // EXCEPT on the shop-all page where we want to show all products
    const currentPath = window.location.pathname;
    const isShopAllPage = currentPath.includes("/collections/shop-all");

    if (
      !isShopAllPage &&
      isGearOrAccessory &&
      (checkedFrameSizes.length > 0 ||
        checkedFrameStyles.length > 0 ||
        checkedLensOptions.length > 0 ||
        checkedFrameColors.length > 0)
    ) {
      hideGridItem(item);
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame size filters
    if (checkedFrameSizes.length > 0) {
      const itemSizes = getAvailableSizes(item);

      const matchesSize = checkedFrameSizes.some((size) => {
        const lowerSize = size.toLowerCase();

        return itemSizes.some((availableSize) => {
          const lowerAvailableSize = availableSize.toLowerCase();

          if (lowerSize === "narrow") {
            return lowerAvailableSize.includes("narrow");
          } else if (lowerSize === "regular") {
            return lowerAvailableSize.includes("regular");
          } else if (lowerSize === "xl") {
            return lowerAvailableSize.includes("xl");
          }

          return lowerAvailableSize === lowerSize;
        });
      });

      if (!matchesSize) {
        matchesAllFilters = false;
      }
    }

    // Check frame style filters
    if (matchesAllFilters && checkedFrameStyles.length > 0) {
      const itemStyleValue = (item.getAttribute("data-style") || "").toLowerCase();
      if (!checkedFrameStyles.includes(itemStyleValue)) {
        matchesAllFilters = false;
      }
    }

    // Check lens option filters
    if (matchesAllFilters && checkedLensOptions.length > 0) {
      const itemLensValue = (
        item.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const matchesLensOption = checkedLensOptions.some((option) => {
        if (option === "polarized") {
          return (
            itemLensValue.includes("polarized") &&
            !itemLensValue.includes("non-polarized") &&
            !itemLensValue.includes("mirror")
          );
        } else if (option === "non-polarized") {
          return (
            itemLensValue.includes("non-polarized") &&
            !itemLensValue.includes("mirror")
          );
        } else if (
          option === "blue-blocking" ||
          option === "low light" ||
          option === "low-light"
        ) {
          return (
            itemLensValue.includes("blue-blocking") ||
            itemLensValue.includes("low light") ||
            itemLensValue.includes("low-light")
          );
        }

        return itemLensValue.includes(option);
      });

      if (!matchesLensOption) {
        matchesAllFilters = false;
      }
    }

    // Check frame color filters
    if (matchesAllFilters && checkedFrameColors.length > 0) {
      const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
      if (!checkedFrameColors.includes(itemColor)) {
        matchesAllFilters = false;
      }
    }

    if (matchesAllFilters) {
      showGridItem(item);
      visibleCount++;
    } else {
      hideGridItem(item);
    }
  });

  debugLog(`applyFilters: Filter applied, ${visibleCount} items visible`);

  // Reorder products based on frame style check order if multiple frame styles are checked
  if (checkedFrameStyles.length > 1 && frameStyleCheckOrder.length > 1) {
    reorderProductsByFrameStyle(productGrid, checkedFrameStyles);
  }

  // Reorder products based on gear check order if multiple gear filters are checked
  if (checkedGearItems.length > 1 && gearCheckOrder.length > 1) {
    reorderProductsByGear(productGrid, checkedGearItems);
  }

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Count all visible products across all containers for accurate no-results display
  const totalVisibleCount = getTotalVisibleProductCount();
  debugLog(
    `applyFilters: Total visible products across all containers: ${totalVisibleCount}`,
  );

  // Show or hide "No results" message
  const showNoResults = totalVisibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // Auto-select frame sizes if filter is active
  if (checkedFrameSizes.length > 0) {
    autoSelectFrameSizes(checkedFrameSizes);
  } else {
    // No size filters active -> reset cards to default size radios
    resetFrameSizeSelection();
  }

  // Update URL parameters to reflect current filter state
  saveFiltersToURL();
}


/**
 * Reorders visible products based on frame style check order (most recent first)
 * @param {Element} productGrid - The product grid container
 * @param {Array} checkedFrameStyles - Array of checked frame style values
 */
function reorderProductsByFrameStyle(productGrid, checkedFrameStyles) {
  debugLog(
    "Reordering products by frame style check order:",
    frameStyleCheckOrder,
  );

  // Get all visible products
  const visibleProducts = Array.from(
    productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this)",
    ),
  );

  if (visibleProducts.length === 0) {
    debugLog("No visible products to reorder");
    return;
  }

  // Group products by frame style
  const productsByStyle = {};
  checkedFrameStyles.forEach((style) => {
    productsByStyle[style] = [];
  });

  visibleProducts.forEach((product) => {
    const productStyle = (
      product.getAttribute("data-style") || ""
    ).toLowerCase();
    if (productsByStyle[productStyle]) {
      productsByStyle[productStyle].push(product);
    }
  });

  debugLog(
    "Products grouped by style:",
    Object.keys(productsByStyle).map(
      (style) => `${style}: ${productsByStyle[style].length} products`,
    ),
  );

  // Create new order based on frameStyleCheckOrder
  const reorderedProducts = [];
  frameStyleCheckOrder.forEach((style) => {
    if (productsByStyle[style] && productsByStyle[style].length > 0) {
      reorderedProducts.push(...productsByStyle[style]);
    }
  });

  // Add any remaining products that might not be in the check order (fallback)
  checkedFrameStyles.forEach((style) => {
    if (!frameStyleCheckOrder.includes(style) && productsByStyle[style]) {
      reorderedProducts.push(...productsByStyle[style]);
    }
  });

  debugLog(`Reordering ${reorderedProducts.length} products`);

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });
}

/**
 * Reorders visible products based on gear check order (most recent first)
 * @param {Element} productGrid - The product grid container
 * @param {Array} checkedGearItems - Array of checked gear values
 */
function reorderProductsByGear(productGrid, checkedGearItems) {
  debugLog("Reordering products by gear check order:", gearCheckOrder);

  // Get all visible products
  const visibleProducts = Array.from(
    productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this)",
    ),
  );

  if (visibleProducts.length === 0) {
    debugLog("No visible products to reorder");
    return;
  }

  // Group products by gear type
  const productsByGear = {};
  checkedGearItems.forEach((gear) => {
    productsByGear[gear] = [];
  });

  visibleProducts.forEach((product) => {
    const itemTitle = (product.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (product.getAttribute("data-style") || "").toLowerCase();

    // Determine which gear type this product belongs to
    let productGearType = null;
    for (const gearType of checkedGearItems) {
      if (
        itemGear.includes(gearType) ||
        (gearType === "shields" &&
          (itemTitle.includes("side shields") ||
            itemStyle.includes("shields"))) ||
        (gearType === "nose-shield" &&
          (itemTitle.includes("nose shield") ||
            itemStyle.includes("nose-shield"))) ||
        (gearType === "risers" &&
          (itemTitle.includes("nose pad") ||
            itemTitle.includes("risers") ||
            itemStyle.includes("riser"))) ||
        (gearType === "floatie" && itemTitle.includes("floatie")) ||
        (gearType === "case" && itemTitle.includes("case")) ||
        (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
        (gearType === "sticker" && itemTitle.includes("sticker")) ||
        (gearType === "giftcard" && itemTitle.includes("gift card"))
      ) {
        productGearType = gearType;
        break;
      }
    }

    if (productGearType && productsByGear[productGearType]) {
      productsByGear[productGearType].push(product);
    }
  });

  debugLog(
    "Products grouped by gear:",
    Object.keys(productsByGear).map(
      (gear) => `${gear}: ${productsByGear[gear].length} products`,
    ),
  );

  // Create new order based on gearCheckOrder
  const reorderedProducts = [];
  gearCheckOrder.forEach((gear) => {
    if (productsByGear[gear] && productsByGear[gear].length > 0) {
      reorderedProducts.push(...productsByGear[gear]);
    }
  });

  // Add any remaining products that might not be in the check order (fallback)
  checkedGearItems.forEach((gear) => {
    if (!gearCheckOrder.includes(gear) && productsByGear[gear]) {
      reorderedProducts.push(...productsByGear[gear]);
    }
  });

  debugLog(`Reordering ${reorderedProducts.length} products by gear`);

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });
}

/**
 * Reorders all products by type on shop-all page (frames first, then gear/accessories)
 * @param {Element} productGrid - The product grid container
 */
function reorderProductsByType(productGrid) {
  debugLog(
    "Reordering products by type (frames first, then gear) on shop-all page",
  );

  // Get all products in the grid
  const allProducts = Array.from(
    productGrid.querySelectorAll(".ombraz-product-v2__grid-item"),
  );

  if (allProducts.length === 0) {
    debugLog("No products to reorder by type");
    return;
  }

  // Separate products into frames and gear/accessories
  const frameProducts = [];
  const gearProducts = [];

  allProducts.forEach((product) => {
    const itemTitle = (product.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (product.getAttribute("data-style") || "").toLowerCase();

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    if (isGearOrAccessory) {
      gearProducts.push(product);
    } else {
      frameProducts.push(product);
    }
  });

  debugLog(
    `Reordering by type: ${frameProducts.length} frames, ${gearProducts.length} gear items`,
  );

  // Combine arrays: frames first, then gear
  const reorderedProducts = [...frameProducts, ...gearProducts];

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });

  debugLog("Products reordered by type on shop-all page");
}

// Helper function to get active filters by type
function getActiveFilters(filterType) {
  const attributeName = `data-${filterType}`;

  // Get all checked checkboxes for this filter type
  const checkedCheckboxes = document.querySelectorAll(
    `input[data-filter-type="${filterType}"]:checked`,
  );

  // Map to get the filter values
  return Array.from(checkedCheckboxes)
    .map((cb) => {
      // Get the value from the appropriate data attribute
      const value = cb.getAttribute(attributeName);
      return value ? value.toLowerCase() : "";
    })
    .filter((value) => value !== ""); // Remove any empty values
}

// Helper function to count all visible products across all containers
function getTotalVisibleProductCount() {
  let totalCount = 0;

  // If a gear filter is active, ignore non-gear containers to avoid false positives
  const gearFiltersActive = getActiveFilters("gear").length > 0;

  // Count visible products in regular product grid
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  if (
    regularProductGrid &&
    !regularProductGrid.classList.contains("hide-this")
  ) {
    const regularProducts = regularProductGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this):not(.visually-hidden)",
    );
    totalCount += regularProducts.length;
    debugLog(
      `Regular product grid visible products: ${regularProducts.length}`,
    );
  }

  // Count visible products in best seller container
  if (!gearFiltersActive) {
    const bestSellerContainer = document.querySelector(
      "[data-custom-products]",
    );
    if (
      bestSellerContainer &&
      !bestSellerContainer.classList.contains("hide-this")
    ) {
      const bestSellerProducts = bestSellerContainer.querySelectorAll(
        ".bs-product-card-v2__list-item:not(.hide-this)",
      );
      totalCount += bestSellerProducts.length;
      debugLog(
        `Best seller container visible products: ${bestSellerProducts.length}`,
      );
    }
  }

  debugLog(`Total visible products across all containers: ${totalCount}`);

  // Safety check: if we have visible products but a "no results" message is showing, force hide it
  if (totalCount > 0) {
    const noResultsMessage = document.getElementById("noResultsMessage");
    if (noResultsMessage && !noResultsMessage.classList.contains("hide-this")) {
      debugLog(
        "SAFETY: Force hiding 'no results' message because products are visible",
      );
      noResultsMessage.classList.add("hide-this");
    }
  }

  return totalCount;
}


// Function to display "No Results" message
// Debounced version of displayNoResultsMessage to prevent flashing
let displayNoResultsTimeout;
let lastFilterState = null;

function displayNoResultsMessage(show) {
  debugLog(`displayNoResultsMessage called with show=${show}`);

  // Clear any existing timeout to prevent conflicts
  if (displayNoResultsTimeout) {
    clearTimeout(displayNoResultsTimeout);
    displayNoResultsTimeout = null;
  }

  if (show) {
    const actualVisibleCount = getTotalVisibleProductCount();
    debugLog(
      `Final check before showing message: ${actualVisibleCount} products visible`,
    );

    if (actualVisibleCount === 0) {
      showNoResultsMessage(true);
    } else {
      debugLog(
        `Preventing false positive: ${actualVisibleCount} products are actually visible`,
      );
    }
  } else {
    const actualVisibleCount = getTotalVisibleProductCount();
    if (actualVisibleCount === 0) {
      debugLog(
        "Skip hiding 'no results' message because no products are visible",
      );
      return;
    }
    displayNoResultsTimeout = setTimeout(() => {
      showNoResultsMessage(false);
      displayNoResultsTimeout = null;
    }, 50);
  }
}

// Internal function to actually show/hide the message
function showNoResultsMessage(show = true) {
  debugLog(`showNoResultsMessage called with show=${show}`);

  // Find the appropriate container to show the message in
  const container = findVisibleContainer();
  let messageElement = document.getElementById("noResultsMessage");

  if (show) {
    debugLog("Creating or showing No Results message");
    if (!messageElement) {
      // Create the message element if it doesn't exist
      messageElement = document.createElement("div");
      messageElement.id = "noResultsMessage";
      messageElement.className = "ombraz-no-results-message";

      messageElement.innerHTML = `
        <div class="ombraz-no-results-container" role="alert" aria-live="assertive">
          <svg class="hidden" aria-label="hidden" width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M15 9L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h2 class="ob-h2">Oh no, big sad!</h2>
          <p class="ob-body">No products match your selected filters :'(
          Adjust the filters to uncover more results</p>
          <button id="clearFiltersBtn" class="ombraz-clear-filters-btn ob-body">Clear All Filters</button>
        </div>
      `;

      // Append to the appropriate container
      if (container) {
        debugLog(
          `Appending No Results message to container: ${container.className}`,
        );
        container.appendChild(messageElement);

        // Add event listener to the Clear Filters button
        const clearBtn = messageElement.querySelector("#clearFiltersBtn");
        if (clearBtn) {
          clearBtn.addEventListener("click", function () {
            // Simulate a click on the existing Clear All button
            const clearAllBtn =
              document.getElementById("clear-all") ||
              document.getElementById("clear-all-mobile");
            if (clearAllBtn) {
              clearAllBtn.click();
            }
          });
        }
      } else {
        debugLog("No container found to append No Results message");
      }
    } else {
      // If the message already exists, make sure it's in the correct container
      if (container && messageElement.parentElement !== container) {
        container.appendChild(messageElement);
      }
      messageElement.classList.remove("hide-this");
    }
  } else if (messageElement) {
    // Hide the message if it exists
    debugLog("Hiding No Results message");
    messageElement.classList.add("hide-this");
  }
}

// Helper function to find the appropriate container to show the "no results" message
function findVisibleContainer() {
  debugLog("Finding visible container for No Results message");

  const gearFiltersActive = getActiveFilters("gear").length > 0;
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;

  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const bestSellerContainer = document.querySelector("[data-custom-products]");

  if (gearFiltersActive && regularProductGrid) {
    debugLog("Gear filters active, using regular product grid for No Results");
    return regularProductGrid;
  }

  // Check which container is currently visible
  if (
    bestSellerChecked &&
    bestSellerContainer &&
    !bestSellerContainer.classList.contains("hide-this")
  ) {
    debugLog("Using best seller container for No Results message");
    return bestSellerContainer;
  } else if (
    regularProductGrid &&
    !regularProductGrid.classList.contains("hide-this")
  ) {
    debugLog("Using regular product grid for No Results message");
    return regularProductGrid;
  }

  // Fallback to the first available container in case none are visible
  return regularProductGrid || bestSellerContainer;
}


// Helper function to check if an element is fully visible in viewport
function isElementInViewport(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Consider element visible if at least 200px of it is in the viewport
  // This provides a better UX by not scrolling when user can already see enough of the content
  const visibleHeight =
    Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
  return visibleHeight >= 200;
}

function uncheckFilters(filterType) {
  const checkboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]`,
  );
  checkboxes.forEach((checkbox) => (checkbox.checked = false));
}

function uncheckLensOptions() {
  const lensOptionCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"][data-filter-type="lens-option"]',
  );
  lensOptionCheckboxes.forEach((checkbox) => (checkbox.checked = false));
}

function getCheckedValues(filterType) {
  const checkedCheckboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]:checked`,
  );

  // Use the right attribute name based on filter type
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  return Array.from(checkedCheckboxes).map((cb) =>
    (cb.getAttribute(attributeName) || "").toLowerCase(),
  );
}

function getAvailableSizes(item) {
  const sizes = [];

  // First check data attributes (best method)
  const sizeAttr = item.getAttribute("data-sizes");
  if (sizeAttr) {
    return sizeAttr
      .toLowerCase()
      .split(",")
      .map((s) => s.trim());
  }

  // Check data-size attribute
  const sizeSingle = item.getAttribute("data-size");
  if (sizeSingle) {
    sizes.push(sizeSingle.toLowerCase().trim());
    return sizes;
  }

  // Check for variant selectors with size information
  const sizeSelectors = item.querySelectorAll(".variant-size-selector");
  if (sizeSelectors && sizeSelectors.length > 0) {
    sizeSelectors.forEach((selector) => {
      const sizeValue = selector.getAttribute("data-value");
      if (sizeValue) {
        sizes.push(sizeValue.toLowerCase().trim());
      }
    });

    if (sizes.length > 0) {
      return sizes;
    }
  }

  // Fallback to checking input elements
  if (
    item.querySelector('input[id^="regular-"]') ||
    item.querySelector('[data-size="regular"]')
  ) {
    sizes.push("regular");
  }

  if (
    item.querySelector('input[id^="narrow-"]') ||
    item.querySelector('[data-size="narrow"]')
  ) {
    sizes.push("narrow");
  }

  if (
    item.querySelector('input[id^="xl-"]') ||
    item.querySelector('[data-size="xl"]')
  ) {
    sizes.push("xl");
  }

  return sizes;
}

function handleURLBasedFiltering() {
  // Check for URL parameters related to filtering
  const urlParams = new URLSearchParams(window.location.search);
  const currentPath = window.location.pathname;

  // First check for URL parameters that should override collection defaults
  const hasURLFilters =
    urlParams.has("styles") ||
    urlParams.has("sizes") ||
    urlParams.has("lenses") ||
    urlParams.has("colors") ||
    urlParams.has("gear") ||
    urlParams.has("limitededition") ||
    urlParams.has("bestseller");

  // If we have URL parameters, prioritize those over collection-specific logic
  if (hasURLFilters) {
    debugLog("Found URL parameters, applying filter restoration");

    // Apply URL parameter restoration
    const filtersRestored = restoreFiltersFromURL();

    if (filtersRestored) {
      return; // Exit early since we've handled URL parameters
    }
  }

  // First check for the sizes parameter which can contain comma-separated values
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Found sizes parameter with values:", sizeValues);

    // Apply each size filter
    sizeValues.forEach((size) => {
      if (["narrow", "regular", "xl"].includes(size.toLowerCase())) {
        queryFilterBySize(size.toLowerCase());
      }
    });

    toggleShowResultsButton(true);
    toggleClearButton(true);
    applyFilters();

    return;
  }

  // Continue with the rest of the function for collection paths
  if (currentPath.includes("/collections/mirrored")) {
    setFilterOption("lens-option", "mirror");
  } else if (currentPath.includes("/collections/best-sellers")) {
    setBestSellerFilter();
  } else if (currentPath.includes("/collections/non-polarized")) {
    setFilterOption("lens-option", "non-polarized");
  } else if (currentPath.includes("/collections/polarized")) {
    setFilterOption("lens-option", "polarized");
  } else if (currentPath.includes("/collections/blue-blocker")) {
    setFilterOption("lens-option", "low-light");
  } else if (currentPath.includes("/collections/low-light")) {
    setFilterOption("lens-option", "low-light");
  } else if (currentPath.includes("/collections/gear")) {
    if (urlParams.has("sideshields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("noseshields") || urlParams.has("nose-shield")) {
      setFilterOption("gear", "nose-shield");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else {
      setAllGearOptions();
    }
  } else {
    // Check if any size parameter exists in the old format
    if (urlParams.has("narrow")) {
      queryFilterBySize("narrow");
    } else if (urlParams.has("regular")) {
      queryFilterBySize("regular");
    } else if (urlParams.has("xl")) {
      queryFilterBySize("xl");
    }

    // Check for frame style parameters
    if (urlParams.has("classic")) {
      setFilterOption("frame-style", "classic");
    } else if (urlParams.has("dolomite")) {
      setFilterOption("frame-style", "dolomite");
    } else if (urlParams.has("leggero")) {
      setFilterOption("frame-style", "leggero");
    } else if (urlParams.has("cammina")) {
      setFilterOption("frame-style", "cammina");
    } else if (urlParams.has("viale")) {
      setFilterOption("frame-style", "viale");
    } else if (urlParams.has("teton")) {
      setFilterOption("frame-style", "teton");
    }

    // Check for gear parameters
    if (urlParams.has("shields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("nose-shield") || urlParams.has("noseshields")) {
      setFilterOption("gear", "nose-shield");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else if (urlParams.has("floatie")) {
      setFilterOption("gear", "floatie");
    } else if (urlParams.has("case")) {
      setFilterOption("gear", "case");
    } else if (urlParams.has("cordreplacement")) {
      setFilterOption("gear", "cordreplacement");
    } else if (urlParams.has("sticker")) {
      setFilterOption("gear", "sticker");
    } else if (urlParams.has("giftcard")) {
      setFilterOption("gear", "giftcard");
    }

    // Check for lens options
    if (urlParams.has("polarized")) {
      setFilterOption("lens-option", "polarized");
    } else if (urlParams.has("non-polarized")) {
      setFilterOption("lens-option", "non-polarized");
    } else if (urlParams.has("blue-blocking") || urlParams.has("low-light")) {
      setFilterOption("lens-option", "blue-blocking");
      setFilterOption("lens-option", "low-light");
    } else if (urlParams.has("photochromic")) {
      setFilterOption("lens-option", "photochromic");
    } else if (urlParams.has("mirror")) {
      setFilterOption("lens-option", "mirror");
    }

    // Check for frame color
    if (urlParams.has("charcoal")) {
      setFilterOption("frame-color", "charcoal");
    } else if (urlParams.has("dusk")) {
      setFilterOption("frame-color", "dusk");
    } else if (urlParams.has("honey")) {
      setFilterOption("frame-color", "honey");
    } else if (urlParams.has("matte-brown")) {
      setFilterOption("frame-color", "matte-brown");
    } else if (urlParams.has("tortoise")) {
      setFilterOption("frame-color", "Tortoise");
    }

    // Check for best-seller and apply if needed
    if (urlParams.has("best-sellers")) {
      setBestSellerFilter();
    }
  }

  // If any filter is active, make sure the filter UI matches
  const hasActiveFilters =
    urlParams.has("narrow") ||
    urlParams.has("regular") ||
    urlParams.has("xl") ||
    urlParams.has("sizes") ||
    urlParams.has("styles") ||
    urlParams.has("classic") ||
    urlParams.has("dolomite") ||
    urlParams.has("leggero") ||
    urlParams.has("cammina") ||
    urlParams.has("viale") ||
    urlParams.has("teton") ||
    urlParams.has("shields") ||
    urlParams.has("risers") ||
    urlParams.has("floatie") ||
    urlParams.has("case") ||
    urlParams.has("cordreplacement") ||
    urlParams.has("sticker") ||
    urlParams.has("giftcard") ||
    urlParams.has("polarized") ||
    urlParams.has("non-polarized") ||
    urlParams.has("blue-blocking") ||
    urlParams.has("low-light") ||
    urlParams.has("mirror") ||
    urlParams.has("charcoal") ||
    urlParams.has("dusk") ||
    urlParams.has("honey") ||
    urlParams.has("matte-brown") ||
    urlParams.has("tortoise") ||
    urlParams.has("limitededition") ||
    urlParams.has("best-sellers") ||
    currentPath.includes("/collections/mirrored") ||
    currentPath.includes("/collections/best-sellers") ||
    currentPath.includes("/collections/non-polarized") ||
    currentPath.includes("/collections/polarized") ||
    currentPath.includes("/collections/blue-blocker") ||
    currentPath.includes("/collections/gear");

  if (hasActiveFilters) {
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Apply filters immediately for mobile
    if (isMobile) {
      applyFilters();
    }
  }
}


function setBestSellerFilter() {
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]',
  );
  if (bestSellerCheckbox) {
    bestSellerCheckbox.checked = true;
    handleBestSellerFilter();
  }
}

function setFilterOption(filterType, optionValue) {
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  const checkbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="${filterType}"][${attributeName}="${optionValue}"]`,
  );

  if (checkbox) {
    const details = checkbox.closest(".ombraz-accordion-item");
    if (details) {
      details.setAttribute("open", "");
      updateAccordionIcon(details, true);
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));

      // Show buttons on mobile
      if (isMobile) {
        toggleShowResultsButton(true);
        toggleClearButton(true);
      }
    }
  }
}

function setAllGearOptions() {
  const gearCheckboxes = document.querySelectorAll(
    'input[type="checkbox"][data-filter-type="gear"]',
  );
  const gearDetails = gearCheckboxes[0]?.closest(".ombraz-accordion-item");
  if (gearDetails) {
    gearDetails.setAttribute("open", "");
    updateAccordionIcon(gearDetails, true);
    gearCheckboxes.forEach((checkbox) => {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));
    });

    // Show buttons on mobile
    if (isMobile) {
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }
  }
}

function updateAccordionIcon(accordionItem, isOpen) {
  const icon = accordionItem.querySelector(".ombraz-icon");
  if (icon) {
    if (isOpen) {
      icon.classList.remove("ombraz-icon-plus");
      icon.classList.add("ombraz-icon-minus");
    } else {
      icon.classList.remove("ombraz-icon-minus");
      icon.classList.add("ombraz-icon-plus");
    }
  }
}

function initializeAccordion() {
  const accordionRoot = document.querySelector(".ombraz-accordion");
  if (!accordionRoot || accordionRoot.dataset.accordionInit === "true") return;
  accordionRoot.dataset.accordionInit = "true";

  const accordionItems = document.querySelectorAll(".ombraz-accordion-item");

  accordionItems.forEach((item) => {
    const summary = item.querySelector("summary");
    const icon = summary?.querySelector(".ombraz-icon");

    if (summary) {
      // Set initial icon state
      if (icon) {
        const isOpen = item.hasAttribute("open");
        icon.classList.toggle("ombraz-icon-minus", isOpen);
        icon.classList.toggle("ombraz-icon-plus", !isOpen);
      }

      summary.addEventListener("click", (e) => {
        e.preventDefault();
        const isOpen = item.hasAttribute("open");

        // Close all other accordion items
        accordionItems.forEach((otherItem) => {
          if (otherItem !== item && otherItem.hasAttribute("open")) {
            otherItem.removeAttribute("open");
            const otherIcon = otherItem.querySelector(".ombraz-icon");
            if (otherIcon) {
              otherIcon.classList.remove("ombraz-icon-minus");
              otherIcon.classList.add("ombraz-icon-plus");
            }
          }
        });

        // Toggle current accordion
        item.toggleAttribute("open");

        // Update icon
        if (icon) {
          if (!isOpen) {
            icon.classList.remove("ombraz-icon-plus");
            icon.classList.add("ombraz-icon-minus");
          } else {
            icon.classList.remove("ombraz-icon-minus");
            icon.classList.add("ombraz-icon-plus");
          }
        }
      });
    }
  });
}

function clearAllFilters() {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  function handleClearAll() {
    const allCheckboxes = document.querySelectorAll(
      '.ombraz-accordion-content input[type="checkbox"]:checked, .ombraz-best-seller input[type="checkbox"]:checked, .ombraz-limited-edition input[type="checkbox"]:checked',
    );

    // Get current URL path to check if we're on a collection page
    const currentPath = window.location.pathname;
    const isOnCollectionPage =
      currentPath.includes("/collections/polarized") ||
      currentPath.includes("/collections/non-polarized") ||
      currentPath.includes("/collections/blue-blocker") ||
      currentPath.includes("/collections/mirrored");

    // Get best seller checkbox and limited edition checkbox
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]',
    );
    const limitedEditionCheckbox = document.querySelector(
      '.ombraz-limited-edition input[type="checkbox"]',
    );
    const regularProductGrid = document.querySelector(
      ".ombraz-product-v2__grid",
    );
    const bestSellerProducts = document.querySelector("[data-custom-products]");

    if (allCheckboxes.length) {
      allCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change"));
      });
    }

    // Clear Best Seller filter
    if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      bestSellerCheckbox.checked = false;
      handleBestSellerFilter();
    }

    // Clear Limited Edition filter
    if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
      limitedEditionCheckbox.checked = false;
      handleLimitedEditionFilter();
    }

    // Show regular product grid and hide special sections
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");

      // Show all products in regular grid
      const regularProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item",
      );
      regularProducts.forEach((product) => {
        if (isMirroredCollectionCard(product)) {
          hideGridItem(product);
        } else {
          showGridItem(product);
          product.classList.remove("hide-this-by-filter");
        }
      });
    }

    // Hide best seller products section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }

    // Hide both buttons
    toggleClearButton(false);
    toggleShowResultsButton(false);

    // Clear URL parameters without page reload
    window.history.replaceState({}, "", window.location.pathname);

    // Clear frame style check order
    frameStyleCheckOrder = [];
    debugLog("Cleared frame style check order");

    // Clear gear check order
    gearCheckOrder = [];
    debugLog("Cleared gear check order");

    // If we're on the shop-all page, reorder products by type (frames first, then gear)
    const isShopAllPage = currentPath.includes("/collections/shop-all");
    if (isShopAllPage && regularProductGrid) {
      reorderProductsByType(regularProductGrid);
    }

    // If we're on a special lens collection page, show those items by their lens type
    if (isOnCollectionPage) {
      // Reset any hiding that might have been applied
      resetAllProductVisibility();

      // Find the lens option that should be visible based on the collection page
      let lensType = "";
      if (currentPath.includes("/collections/polarized")) {
        lensType = "polarized";
      } else if (currentPath.includes("/collections/non-polarized")) {
        lensType = "non-polarized";
      } else if (currentPath.includes("/collections/blue-blocker")) {
        lensType = "low-light";
      } else if (currentPath.includes("/collections/low-light")) {
        lensType = "low-light";
      } else if (currentPath.includes("/collections/mirrored")) {
        lensType = "mirror";
      }

      // Show only products matching the lens type of current collection
      if (lensType && regularProductGrid) {
        const products = regularProductGrid.querySelectorAll(
          ".ombraz-product-v2__grid-item",
        );

        // First show all products to avoid flickering
        products.forEach((product) => {
          product.classList.remove("hide-this-by-filter");
          if (isMirroredCollectionCard(product)) {
            hideGridItem(product);
          } else {
            showGridItem(product);
          }
        });

        // Then hide products that don't match the lens type
        requestAnimationFrame(() => {
          products.forEach((product) => {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();

            let showItem = false;
            if (lensType === "polarized") {
              showItem =
                itemLensValue.includes("polarized") &&
                !itemLensValue.includes("non-polarized");
            } else if (lensType === "non-polarized") {
              showItem = itemLensValue.includes("non-polarized");
            } else if (
              lensType === "blue-blocking" ||
              lensType === "low-light"
            ) {
              showItem =
                itemLensValue.includes("blue-blocking") ||
                itemLensValue.includes("low-light");
            } else if (lensType === "mirror") {
              showItem = isMirroredCollectionCard(product);
            }

            if (showItem) {
              showGridItem(product);
              product.classList.remove("hide-this-by-filter");
            } else {
              hideGridItem(product);
              product.classList.add("hide-this-by-filter");
            }
          });
        });
      }
    }

    if (currentPath.includes("/collections/mirrored")) {
      const mirrorCheckbox = document.querySelector(
        'input[data-filter-type="lens-option"][data-lens-option="mirror"]',
      );
      if (mirrorCheckbox) {
        mirrorCheckbox.checked = true;
        const accordion = mirrorCheckbox.closest(".ombraz-accordion-item");
        if (accordion) {
          accordion.setAttribute("open", "");
          updateAccordionIcon(accordion, true);
        }
      }
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }

    // Hide "No results" message if it exists
    displayNoResultsMessage(false);
  }

  // Desktop clear all
  if (clearButtonDesktop) {
    clearButtonDesktop.addEventListener("click", function () {
      handleClearAll();
      if (!isMobile) {
        applyFilters();
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }

  // Mobile clear all
  if (clearButtonMobile) {
    clearButtonMobile.addEventListener("click", function () {
      handleClearAll();
      if (isMobile) {
        applyFilters();
        closeTheFilterHederMobile();
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }
}


function handleBestSellerFilter(fromShowResultsButton = false) {
  debugLog("handleBestSellerFilter called", {
    fromShowResultsButton,
    isMobile,
  });

  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]',
  );
  const bestSellerProducts = document.querySelector("[data-custom-products]");
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");

  // Get all checked filter values
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  const hasActiveFilters =
    checkedFrameSizes.length > 0 ||
    checkedFrameStyles.length > 0 ||
    checkedLensOptions.length > 0 ||
    checkedFrameColors.length > 0 ||
    checkedGearItems.length > 0;

  if (bestSellerCheckbox?.checked) {
    // Show Best Seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.remove("hide-this", "visually-hidden");

      // Filter best seller products if other filters are active
      if (hasActiveFilters) {
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item",
        );

        // Function to check if a product matches all active filters
        const productMatchesFilters = (product) => {
          let matchesAllFilters = true;

          // Check frame size
          if (checkedFrameSizes.length > 0) {
            const itemSize = (
              product.getAttribute("data-size") || ""
            ).toLowerCase();
            const matchesSize = checkedFrameSizes.some((size) =>
              itemSize.includes(size.toLowerCase()),
            );
            if (!matchesSize) matchesAllFilters = false;
          }

          // Check frame style
          if (matchesAllFilters && checkedFrameStyles.length > 0) {
            const itemStyle = (
              product.getAttribute("data-style") || ""
            ).toLowerCase();
            if (!checkedFrameStyles.includes(itemStyle))
              matchesAllFilters = false;
          }

          // Check lens options
          if (matchesAllFilters && checkedLensOptions.length > 0) {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();
            const matchesLensOption = checkedLensOptions.some((option) => {
              if (option === "polarized") {
                return (
                  itemLensValue.includes("polarized") &&
                  !itemLensValue.includes("non-polarized")
                );
              } else if (option === "non-polarized") {
                return itemLensValue.includes("non-polarized");
              } else if (option === "blue-blocking" || option === "low-light") {
                return (
                  itemLensValue.includes("blue-blocking") ||
                  itemLensValue.includes("low-light")
                );
              }
              return itemLensValue.includes(option);
            });
            if (!matchesLensOption) matchesAllFilters = false;
          }

          // Check frame color
          if (matchesAllFilters && checkedFrameColors.length > 0) {
            const itemColor = (
              product.getAttribute("data-color") || ""
            ).toLowerCase();
            if (!checkedFrameColors.includes(itemColor))
              matchesAllFilters = false;
          }

          return matchesAllFilters;
        };

        // Apply filtering to best seller items
        bsItems.forEach((item) => {
          if (productMatchesFilters(item)) {
            item.classList.remove("hide-this");
          } else {
            item.classList.add("hide-this");
          }
        });

        // Show No Results message if no items are visible
        const totalVisibleCount = getTotalVisibleProductCount();
        const showNoResults = totalVisibleCount === 0 && hasActiveFilters;
        displayNoResultsMessage(showNoResults);
      } else {
        // No active filters, show all best seller products
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item",
        );
        bsItems.forEach((item) => item.classList.remove("hide-this"));

        displayNoResultsMessage(false);
      }
    }

    // Hide regular product grid completely
    if (regularProductGrid) {
      regularProductGrid.classList.add("hide-this", "visually-hidden");
    }

    if (isMobile) {
      closeTheFilterHederMobile();
    }
  } else {
    // Best Seller is unchecked - show regular grid
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");
    }

    if (hasActiveFilters) {
      applyFilters();
    } else {
      const regularProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item",
      );
      regularProducts.forEach((product) => {
        if (isMirroredCollectionCard(product)) {
          hideGridItem(product);
        } else {
          showGridItem(product);
        }
      });

      displayNoResultsMessage(false);
    }

    if (isMobile) {
      closeTheFilterHederMobile();
    }
  }

  // Update UI states
  toggleClearButton(bestSellerCheckbox?.checked || hasActiveFilters);

  // Update URL parameters
  saveFiltersToURL();
}


function handleLimitedEditionFilter(fromShowResultsButton = false) {
  const limitedEditionCheckbox = document.querySelector(
    '.ombraz-limited-edition input[type="checkbox"]',
  );

  if (!limitedEditionCheckbox) {
    debugLog("Limited Edition checkbox not found");
    return;
  }

  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const bestSellerProducts = document.querySelector("[data-custom-products]");

  // Check if any other filters are active
  const filterCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"]',
  );
  const hasActiveFilters = Array.from(filterCheckboxes).some(
    (checkbox) => checkbox.checked,
  );

  if (limitedEditionCheckbox.checked) {
    debugLog("Limited Edition filter is checked");

    // Hide best seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show only Limited Edition products in regular grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");

      const allProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item",
      );

      allProducts.forEach((product) => {
        if (isMirroredCollectionCard(product)) {
          hideGridItem(product);
          return;
        }

        const isLimitedEdition = productSupportsLimitedEdition(product);

        if (isLimitedEdition) {
          showGridItem(product);
        } else {
          hideGridItem(product);
        }
      });
    }
  } else {
    // LE is unchecked - show normal collection view
    debugLog("Limited Edition filter is unchecked");

    // Always hide Best Seller section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show regular grid - remove LE filtering
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");

      const allProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item",
      );

      // Remove LE-specific hiding - let other filters handle visibility
      allProducts.forEach((product) => {
        if (isMirroredCollectionCard(product)) {
          hideGridItem(product);
        } else {
          showGridItem(product);
        }
      });
    }
  }

  // Update UI states
  toggleShowResultsButton(limitedEditionCheckbox.checked || hasActiveFilters);
  toggleClearButton(limitedEditionCheckbox.checked || hasActiveFilters);
  dispatchLimitedEditionModeChange(limitedEditionCheckbox.checked);

  // Update URL parameters
  saveFiltersToURL();
}


function toggleShowResultsButton(show) {
  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn) {
    showResultsBtn.classList.toggle("hide-this", !show);
  }
}

function toggleClearButton(show) {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  if (clearButtonDesktop) {
    clearButtonDesktop.classList.toggle("hide-this", !show);
  }

  if (clearButtonMobile) {
    clearButtonMobile.classList.toggle("hide-this", !show);
  }
}

function closeTheFilterHederMobile() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");
  const filterToggle = document.getElementById("ombrazFilterToggle");

  if (sidebar) {
    sidebar.classList.remove("active");
    if (filterWrapper) {
      filterWrapper.classList.remove("is-full-screen");
    }

    // Restore scroll position and unlock body
    document.body.classList.remove("filter-active");
    const scrollPositionValue = document.documentElement.style.getPropertyValue(
      "--scroll-position",
    );
    document.documentElement.style.removeProperty("--scroll-position");

    const parsedScrollPosition = Number.parseInt(scrollPositionValue, 10);
    if (Number.isFinite(parsedScrollPosition)) {
      window.scrollTo(0, Math.abs(parsedScrollPosition));
    }
  }

  if (filterToggle) {
    filterToggle.setAttribute("aria-expanded", "false");
    const icon = filterToggle.querySelector(".ombraz-icon");
    if (icon) {
      icon.classList.remove("ombraz-icon-chevron-up");
      icon.classList.add("ombraz-icon-chevron-down");
    }
  }
}

function uncheckAllFilters() {
  const allCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"], .ombraz-best-seller input[type="checkbox"]',
  );
  allCheckboxes.forEach((checkbox) => {
    checkbox.checked = false;
  });
}



function toggleFilter() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");

  if (sidebar) {
    const isActive = sidebar.classList.contains("active");

    if (!isActive) {
      // Opening the filter
      sidebar.classList.add("active");
      if (filterWrapper) {
        filterWrapper.classList.add("is-full-screen");
      }

      // Store current scroll position and lock body
      document.body.classList.add("filter-active");
    } else {
      // Closing the filter
      closeTheFilterHederMobile();
    }
  }
}

// Function to query and filter products by size from URL parameter
function queryFilterBySize(size) {
  debugLog(`Filtering by size: ${size}`);

  // Normalize the size value
  const normalizedSize = size.toLowerCase().trim();

  // Get all product cards
  const productCards = document.querySelectorAll(
    ".ombraz-product-v2__grid-item",
  );

  // Track if we've found the checkbox for this size
  let foundCheckbox = false;

  // Find and check the corresponding size checkbox
  const sizeCheckbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="frame-size"][data-frame-size="${normalizedSize}"]`,
  );

  if (sizeCheckbox) {
    foundCheckbox = true;
    debugLog(`Found checkbox for size: ${normalizedSize}`);

    // Open the accordion containing this checkbox
    const accordion = sizeCheckbox.closest(".ombraz-accordion-item");
    if (accordion) {
      accordion.setAttribute("open", "");
      updateAccordionIcon(accordion, true);
    }

    // Check the checkbox
    sizeCheckbox.checked = true;

    // Don't trigger change event yet - we'll apply all filters at once
  } else {
    debugLog(`Could not find checkbox for size: ${normalizedSize}`);
  }

  // If we didn't find a checkbox, we'll manually filter the products
  if (!foundCheckbox) {
    debugLog(`Manually filtering products by size: ${normalizedSize}`);

    productCards.forEach((card) => {
      if (isMirroredCollectionCard(card)) {
        hideGridItem(card);
        return;
      }

      // Use our improved size detection function
      const itemSizes = getAvailableSizes(card);
      const hasSize = itemSizes.includes(normalizedSize);

      // Check if this is a gear/accessory item (which don't have sizes)
      const isGearOrAccessory =
        card.getAttribute("data-style")?.includes("shields") ||
        card.getAttribute("data-style")?.includes("nose-shield") ||
        card.getAttribute("data-style")?.includes("riser") ||
        card.getAttribute("data-gear")?.includes("shields") ||
        card.getAttribute("data-gear")?.includes("risers") ||
        card.getAttribute("data-gear")?.includes("case") ||
        card.getAttribute("data-gear")?.includes("floatie") ||
        card.getAttribute("data-gear")?.includes("cordreplacement") ||
        card.getAttribute("data-gear")?.includes("sticker") ||
        card.getAttribute("data-gear")?.includes("giftcard");

      // Hide items that don't match the size or are gear items
      if (isGearOrAccessory || !hasSize) {
        hideGridItem(card);
      }
    });
  }

  return foundCheckbox;
}

// Helper function to reset visibility of all products in the grid
function resetAllProductVisibility() {
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  if (productGrid) {
    const products = productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item",
    );
    products.forEach((product) => {
      product.classList.remove("hide-this-by-filter");
      if (isMirroredCollectionCard(product)) {
        hideGridItem(product);
      } else {
        showGridItem(product);
      }
    });
  }
}

// Function to handle direct size URL parameters
function handleSizeURLParameter() {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if the sizes parameter exists
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Handling direct size URL parameter with values:", sizeValues);

    // Process each size value
    let foundAnySize = false;

    sizeValues.forEach((size) => {
      const normalizedSize = size.toLowerCase().trim();

      // Only process valid sizes
      if (["narrow", "regular", "xl"].includes(normalizedSize)) {
        // Try to find and check the corresponding checkbox
        const found = queryFilterBySize(normalizedSize);
        foundAnySize = foundAnySize || found;
      }
    });

    // If we found and checked any size checkboxes, apply the filters
    if (foundAnySize) {
      // Show filter UI elements
      toggleShowResultsButton(true);
      toggleClearButton(true);

      // Apply filters
      applyFilters();

      return true; // Indicate that we handled the parameter
    }
  }

  return false; // Indicate that we didn't handle any parameter
}
